import { GridFilterModel, GridRowParams } from '@mui/x-data-grid-pro';
import { compact, first, isEmpty } from 'lodash';
import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SpinningIcon } from '@/components/animation/SpinningIcon/SpinningIcon';
import { SmallTableEmptyState } from '@/components/display/SmallTableEmptyState/SmallTableEmptyState';
import { Button } from '@/components/form/baseInputs/Button';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { RefreshCw05Icon } from '@/components/icons/RefreshCw05Icon';
import { Upload01Icon } from '@/components/icons/Upload01Icon';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { TableEmptyState } from '@/components/tables/DataTable/components/TableEmptyState';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { ActionButtonRenderer } from '@/components/tables/DataTable/renderers/cell/ActionButtonRenderer';
import { EmptyCell } from '@/components/tables/DataTable/renderers/cell/EmptyCell';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { getMemoBraintrustLogsUrl } from '@/modules/braintrust/braintrustLogs';
import { useBraintrustLinks } from '@/modules/braintrust/useBraintrustLinks';
import { useClientDocumentsPageContext } from '@/modules/documents/contexts/clientDocumentsPage.context';
import { useCopyMemo } from '@/modules/memos/hooks/useCopyMemo';
import { useCopyMemoContentToClipboard } from '@/modules/memos/hooks/useCopyMemoContentToClipboard';
import { useDeleteMemo } from '@/modules/memos/hooks/useDeleteMemo';
import { useEnqueueMemoExport } from '@/modules/memos/hooks/useEnqueueMemoExport';
import { useEnqueueMemoWordExport } from '@/modules/memos/hooks/useEnqueueMemoWordExport';
import { useMemoGenerationTrackerStateByMemoId } from '@/modules/memos/hooks/useMemoGenerationTrackerState';
import { useOnMemoGenerationComplete } from '@/modules/memos/hooks/useOnMemoGenerationComplete';
import { MEMOS_EMPTY_STATE_DESCRIPTION } from '@/modules/memos/memos.constants';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { COLORS } from '@/styles/tokens/colors';
import { diagnostics } from '@/utils/diagnostics';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';
import { getNodes } from '@/utils/graphqlUtils';

import { MemoContextMenuItems } from '../components/MemoContextMenuItems';
import { useMemoDetailsLazyQuery } from '../graphql/MemoDetails.generated';
import { useMemosTableMemosQuery } from './graphql/MemosTable.generated';
import { MemosSearchToolbar } from './MemosSearchToolbar/MemosSearchToolbar';
import { MemosTableRowData } from './MemosTable.types';
import { mapMemoToRowData } from './MemosTable.utils';

interface MemosTableProps {
  householdId: string;
}

export function MemosTable({ householdId }: MemosTableProps) {
  const navigate = useNavigate();
  const { createErrorFeedback } = useFeedback();
  const canEditHouseholdData = useCanEditHouseholdData();
  const { setModalOpen } = useClientDocumentsPageContext();

  const handleUploadDocuments = useCallback(() => {
    setModalOpen(true);
    navigate(
      getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS, {
        householdId,
      }),
      { replace: true }
    );
  }, [householdId, navigate, setModalOpen]);
  const { showDiagnosticLinks } = useBraintrustLinks();
  const { deleteMemo } = useDeleteMemo();
  const { onExport: exportMemoToPdf } = useEnqueueMemoExport({
    householdId,
    source: 'memos table',
  });
  const { onExport: exportMemoToWord } = useEnqueueMemoWordExport({
    householdId,
    source: 'memos table',
  });
  const copyMemoContentToClipboard = useCopyMemoContentToClipboard();
  const [deletingMemoIds, setDeletingMemoIds] = useState<Set<string>>(
    new Set()
  );
  const [copyingMemoId, setCopyingMemoId] = useState<string | null>(null);
  const [duplicatingMemoId, setDuplicatingMemoId] = useState<string | null>(
    null
  );
  const memoGenerationTrackerStateByMemoId =
    useMemoGenerationTrackerStateByMemoId(householdId);
  const [loadMemoForCopy] = useMemoDetailsLazyQuery({
    fetchPolicy: 'network-only',
    onError: createErrorFeedback('Failed to load memo for copying'),
  });
  const { copyMemo } = useCopyMemo({
    onSuccess: (newMemoId) => {
      navigate(
        getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS, {
          householdId,
          memoId: newMemoId,
        })
      );
    },
  });

  // Search state managed locally so the table never unmounts during search,
  // aligning with the pattern used by the documents table.
  const [searchValue, setSearchValue] = useState('');

  const onFilterModelChange = useCallback(
    ({ quickFilterValues }: GridFilterModel) => {
      const searchString = quickFilterValues?.join(' ') ?? '';
      setSearchValue(searchString);
    },
    []
  );

  const { data, loading, refetch } = useMemosTableMemosQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      householdId,
      searchValue: searchValue || undefined,
    },
    onError: createErrorFeedback(
      `We couldn't load memos. Please refresh and try again.`
    ),
  });

  const rows = useMemo(() => {
    return getNodes(data?.memos).map((memo) => {
      return mapMemoToRowData(memo);
    });
  }, [data?.memos]);

  const hasFilters = Boolean(searchValue);

  useOnMemoGenerationComplete(householdId, () => {
    diagnostics.debug(
      '[MemosTable] Memo generation processing completed, refetching',
      { householdId }
    );
    void refetch();
  });

  const handleDeleteMemo = useCallback(
    async (memoId: string) => {
      setDeletingMemoIds((currentMemoIds) => {
        const nextMemoIds = new Set(currentMemoIds);
        nextMemoIds.add(memoId);
        return nextMemoIds;
      });

      try {
        await deleteMemo(memoId);
      } finally {
        setDeletingMemoIds((currentMemoIds) => {
          const nextMemoIds = new Set(currentMemoIds);
          nextMemoIds.delete(memoId);
          return nextMemoIds;
        });
      }
    },
    [deleteMemo]
  );

  const handleCopyMemoToClipboard = useCallback(
    async (memoId: string) => {
      setCopyingMemoId(memoId);

      try {
        const { data: memoData } = await loadMemoForCopy({
          variables: {
            memoId,
          },
        });

        if (!memoData) {
          return;
        }

        const memo = first(getNodes(memoData.memos)) ?? null;
        copyMemoContentToClipboard(memo?.content);
      } finally {
        setCopyingMemoId((currentMemoId) => {
          return currentMemoId === memoId ? null : currentMemoId;
        });
      }
    },
    [copyMemoContentToClipboard, loadMemoForCopy]
  );

  const handleDuplicateMemo = useCallback(
    async (memoId: string) => {
      setDuplicatingMemoId(memoId);

      try {
        await copyMemo({ householdId, memoId });
      } finally {
        setDuplicatingMemoId((currentMemoId) => {
          return currentMemoId === memoId ? null : currentMemoId;
        });
      }
    },
    [copyMemo, householdId]
  );

  const openMemo = useCallback(
    (memoId: string, openInNewTab = false) => {
      const path = getCompletePathFromRouteKey(
        ROUTE_KEYS.HOUSEHOLD_DETAILS_MEMO_DETAILS,
        {
          householdId,
          memoId,
        }
      );

      if (openInNewTab) {
        window.open(path, '_blank');
        return;
      }

      navigate(path);
    },
    [householdId, navigate]
  );

  const onRowClick = useCallback(
    ({ row }: GridRowParams<MemosTableRowData>, event: MouseEvent) => {
      const openInNewTab = event.metaKey || event.ctrlKey;
      openMemo(row.id, openInNewTab);
    },
    [openMemo]
  );

  const columns: Column<MemosTableRowData>[] = useMemo(() => {
    return compact([
      {
        field: 'name',
        headerName: 'Memo name',
        flex: 1,
        minWidth: 320,
        renderCell: TextRenderer({
          text: ({ row }) => row.name,
        }),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 220,
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => {
            if (memoGenerationTrackerStateByMemoId[row.id]?.isInProgress) {
              return 'Generating...';
            }

            return row.statusLabel;
          },
          lineTwo: ({ row }) => {
            if (memoGenerationTrackerStateByMemoId[row.id]?.isInProgress) {
              return undefined;
            }

            return formatDateToMMDDYY(row.statusDate);
          },
          textAccompaniment: ({ row }) => {
            if (!memoGenerationTrackerStateByMemoId[row.id]?.isInProgress) {
              return null;
            }

            return (
              <SpinningIcon
                Icon={RefreshCw05Icon}
                color={COLORS.TEXT.SECONDARY}
                size={16}
              />
            );
          },
        }),
      },
      {
        field: 'updatedAt',
        headerName: 'Last edited',
        width: 160,
        renderCell: TextRenderer({
          text: ({ row }) => formatDateToMMDDYY(row.updatedAt) ?? <EmptyCell />,
        }),
      },
      {
        field: 'userDisplayName',
        headerName: 'Created by',
        width: 200,
        renderCell: TextRenderer({
          text: ({ row }) => row.userDisplayName,
        }),
      },
      {
        field: 'id',
        headerName: '',
        width: 64,
        minWidth: 64,
        maxWidth: 64,
        sortable: false,
        align: 'center',
        renderCell: ActionButtonRenderer({
          actions: [
            {
              MenuItem: (row) => {
                const deletingMemo = deletingMemoIds.has(row.id);
                const copyingMemo = copyingMemoId === row.id;
                const duplicatingMemo = duplicatingMemoId === row.id;
                const isGenerating =
                  memoGenerationTrackerStateByMemoId[row.id]?.isInProgress ??
                  false;
                const disableNonDeleteActions =
                  isGenerating ||
                  deletingMemo ||
                  copyingMemo ||
                  duplicatingMemo;

                return (
                  <MemoContextMenuItems
                    braintrustLogsUrl={
                      showDiagnosticLinks
                        ? getMemoBraintrustLogsUrl({
                            householdId,
                            memoId: row.id,
                          })
                        : undefined
                    }
                    onExportMemoToPdf={() => {
                      void exportMemoToPdf(row.id);
                    }}
                    onExportMemoToWord={() => {
                      void exportMemoToWord(row.id);
                    }}
                    onCopyMemoToClipboard={() => {
                      void handleCopyMemoToClipboard(row.id);
                    }}
                    onDuplicateMemo={
                      canEditHouseholdData
                        ? () => {
                            void handleDuplicateMemo(row.id);
                          }
                        : undefined
                    }
                    onDeleteMemo={() => {
                      void handleDeleteMemo(row.id);
                    }}
                    deletingMemo={deletingMemo}
                    copyingMemo={copyingMemo}
                    duplicatingMemo={duplicatingMemo}
                    disableNonDeleteActions={disableNonDeleteActions}
                    disableDeleteMemo={
                      deletingMemo || copyingMemo || duplicatingMemo
                    }
                    showDeleteMemo={canEditHouseholdData}
                  />
                );
              },
            },
          ],
        }),
      },
    ]);
  }, [
    copyingMemoId,
    deletingMemoIds,
    duplicatingMemoId,
    exportMemoToPdf,
    exportMemoToWord,
    handleCopyMemoToClipboard,
    handleDeleteMemo,
    handleDuplicateMemo,
    memoGenerationTrackerStateByMemoId,
    canEditHouseholdData,
    showDiagnosticLinks,
    householdId,
  ]);

  const showEmptyState = !loading && isEmpty(rows);

  return (
    <>
      {showEmptyState && !hasFilters && (
        <SmallTableEmptyState
          fill="flex"
          icon={<FileSearch02Icon size={32} color={COLORS.TEXT.PRIMARY} />}
          heading="No memos"
          description={MEMOS_EMPTY_STATE_DESCRIPTION}
          action={
            canEditHouseholdData ? (
              <Button
                variant="primary"
                size="sm"
                startIcon={Upload01Icon}
                onClick={handleUploadDocuments}
              >
                Upload documents
              </Button>
            ) : undefined
          }
        />
      )}
      {(!showEmptyState || hasFilters) && (
        <DataTable
          rows={rows}
          columns={columns}
          loading={loading}
          pageSize="long"
          onRowClick={onRowClick}
          onFilterModelChange={onFilterModelChange}
          filterMode="server"
          slots={{
            toolbar: MemosSearchToolbar,
            noRowsOverlay: () => (
              <TableEmptyState
                text={
                  hasFilters
                    ? 'No memos match your search'
                    : 'No memos available'
                }
              />
            ),
          }}
        />
      )}
    </>
  );
}
